import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Strip HTML tags from a string and return plain text, preserving line breaks
 */
export function stripHtmlTags(html: string): string {
  // Convert <br>, <br/>, <br /> to newlines
  let text = html.replace(/<br\s*\/?>/gi, '\n');
  // Convert closing </p> tags to double newlines (paragraph breaks)
  text = text.replace(/<\/p>/gi, '\n\n');
  // Parse and extract text content
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const result = doc.body.textContent || '';
  // Clean up excessive newlines (more than 2 consecutive)
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Sanitizes a string to be used as a safe filename
 * Removes special characters, truncates to reasonable length, and adds extension
 */
export function sanitizeFilename(title: string, extension: string = 'mp3'): string {
  // Convert to lowercase, replace spaces with hyphens
  let filename = title.toLowerCase();
  // Remove special characters except hyphens and spaces
  filename = filename.replace(/[^a-z0-9\s-]/g, '');
  // Replace multiple spaces/hyphens with single hyphen
  filename = filename.replace(/[\s-]+/g, '-');
  // Trim hyphens from start/end
  filename = filename.replace(/^-+|-+$/g, '');
  // Truncate to reasonable length
  filename = filename.substring(0, 60);
  // Add extension
  return `${filename}.${extension}`;
}

/**
 * Truncate text to a specified character limit with ellipsis
 */
export function truncateText(text: string, maxLength: number = 150): string {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * CJK-aware word count.
 * For Latin-based languages, splits on whitespace.
 * For CJK text (Chinese/Japanese/Korean), counts individual characters
 * since they are not separated by spaces.
 */
export function countWords(text: string): number {
  const whitespaceWords = text.trim().split(/\s+/).filter(Boolean).length;
  const cjkPattern = /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/g;
  const cjkChars = (text.match(cjkPattern) || []).length;
  return cjkChars > whitespaceWords ? cjkChars : whitespaceWords;
}

/**
 * Check if a display name is valid (not garbled/auto-generated).
 * Rejects null, empty, email addresses, and random alphanumeric IDs
 * (common with Apple Sign-In failures).
 */
export function isValidDisplayName(name: string | null | undefined): boolean {
  if (!name || !name.trim()) return false;
  const trimmed = name.trim();
  if (trimmed.includes('@')) return false;
  if (/^[a-z0-9]{8,}$/i.test(trimmed) && !/\s/.test(trimmed)) return false;
  return true;
}

/**
 * Sanitize HTML show notes: keep safe tags, strip dangerous ones,
 * and ensure all links open in a new tab.
 */
const SAFE_TAGS = new Set([
  'a', 'p', 'br', 'ul', 'ol', 'li', 'b', 'strong', 'i', 'em',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'blockquote', 'hr',
]);

/**
 * Generate a URL-safe slug from a show/channel name.
 * Mirrors the server-side generate_slug() function.
 */
export function generateShowSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
}

const URL_REGEX = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/g;

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function autoLinkUrls(escapedText: string): string {
  return escapedText.replace(
    URL_REGEX,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
}

export function sanitizeShowNotesHtml(html: string): string {
  // Detect plain text (no HTML tags) — common for YouTube descriptions
  const hasHtmlTags = /<[a-z][\s\S]*?>/i.test(html);
  if (!hasHtmlTags) {
    let safe = escapeHtml(html);
    safe = autoLinkUrls(safe);
    safe = safe.replace(/\n/g, '<br />');
    return safe;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');

  function walk(node: Node, parentTag: string | null): string {
    if (node.nodeType === Node.TEXT_NODE) {
      const escaped = escapeHtml(node.textContent || '');
      // Skip auto-linking if already inside an <a> to avoid nested links
      return parentTag === 'a' ? escaped : autoLinkUrls(escaped);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    // Recursively process children
    const childHtml = Array.from(el.childNodes).map((n) => walk(n, tag)).join('');

    if (!SAFE_TAGS.has(tag)) {
      // Unsafe tag: keep its text children but drop the tag itself
      return childHtml;
    }

    // Build the sanitized element
    if (tag === 'a') {
      const href = el.getAttribute('href') || '#';
      // Only allow http/https/mailto links
      if (!/^(https?:|mailto:)/i.test(href)) {
        return childHtml;
      }
      return `<a href="${href.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${childHtml}</a>`;
    }

    if (tag === 'br' || tag === 'hr') {
      return `<${tag} />`;
    }

    return `<${tag}>${childHtml}</${tag}>`;
  }

  return Array.from(doc.body.childNodes).map((n) => walk(n, null)).join('');
}
